import i18n from "i18next";
import { faEllipsisVertical, faPen } from "@fortawesome/pro-regular-svg-icons";
import { SalesLeadView } from "src/api/generated/erp/db/types/tables/salesLeadView.ts";
import { getSalesLeadStateLabel } from "src/api/generated/erp/db/types/enums/salesLeadState.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { LabelValueViewItem } from "src/components/common/misc/LabelValueView.tsx";
import { ObjectInfoView } from "src/components/common/misc/ObjectInfoView.tsx";
import { AavoMenuButton } from "src/components/common/menu/AavoMenuButton.tsx";
import { SalesLeadForm } from "src/components/views/erp/sales/crm/unallocatedSalesLeads/SalesLeadForm.tsx";
import { SalesLeadsDataGridRowContextMenu } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadsDataGridRowContextMenu.tsx";
import { formatIsoString } from "src/utils/dayjsUtils.ts";
import { nullIfBlank } from "src/utils/strings.tsx";
import { getCountryLabel } from "src/components/common/country/countries.ts";
import { formatMoney } from "src/utils/numberUtils.ts";
import { getCustomerTypeLabel } from "src/api/generated/erp/db/types/enums/customerType.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { ObjectAttributeValueWithField } from "src/api/generated/erp/common/objectAttributes/model/objectAttributeValueWithField.ts";
import { getObjectAttributeLabelValueSection } from "src/components/views/erp/objectAttributes/objectAttributeLabelValueSection.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";
import { SalesLeadFollowUpStatusIndicator } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadFollowUpStatusIndicator.tsx";

export interface SalesLeadInfoViewProps {
	lead: SalesLeadView;
	attributes: ObjectAttributeValueWithField[];
	refreshData: () => Promise<void>;
}

export const SalesLeadInfoView = ({ lead, attributes, refreshData }: SalesLeadInfoViewProps) => {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const editable = hasPermission(Permissions.Sales.SalesLeads.MANAGE);

	return (
		<ObjectInfoView
			title={lead.customerName}
			subtitleItems={[
				`${i18n.t("sales_lead")} ${lead.salesLeadId}`,
				getSalesLeadStateLabel(lead.salesLeadState),
				<SalesLeadFollowUpStatusIndicator key={"followUpStatus"} status={lead.followUpStatus} showLabel />,
			]}
			keyFacts={[
				{ label: i18n.t("sales_pipeline_phase"), value: lead.salesPipelinePhaseName },
				{
					label: i18n.t("estimated_value"),
					value: lead.estimatedValue != null ? formatMoney(lead.estimatedValue) : null,
				},
				{ label: i18n.t("salesperson"), value: lead.salespersonName },
			]}
			actions={
				editable && (
					<>
						<AsyncButton
							icon={faPen}
							tooltip={i18n.t("edit")}
							size={"small"}
							onClick={() => {
								openFormOnDialog({
									openDialog,
									component: SalesLeadForm,
									props: {
										initialization: {
											type: "existingLead",
											salesLeadId: lead.salesLeadId,
										},
									},
									title: i18n.t("sales_lead"),
									size: "lg",
									agentContext: {
										key: defineAgentUiContextKey("erp.sales.crm.salesLead.form.dialog"),
										title: i18n.t("sales_lead"),
										pathSegments: [i18n.t("form")],
										data: {
											salesLeadId: lead.salesLeadId,
											customerId: lead.customerId ?? null,
											customerOrderOfferId: lead.customerOrderOfferId ?? null,
										},
									},
									onSubmit: async () => {
										await refreshData();
									},
								});
							}}
						/>
						<AavoMenuButton
							icon={faEllipsisVertical}
							tooltip={i18n.t("more")}
							size={"small"}
							menuContent={[
								<CustomUiActionObjectMenuItems
									key={"customFunctionUiActions"}
									view={"SALES_LEADS"}
									objectIds={[lead.salesLeadId]}
									refreshData={refreshData}
								/>,
								<SalesLeadsDataGridRowContextMenu
									key={"salesLeadActions"}
									row={lead}
									allSelectedRows={[lead]}
									refreshData={refreshData}
									editable={editable}
								/>,
							]}
						/>
					</>
				)
			}
			items={getSalesLeadInfoItems(lead, attributes)}
		/>
	);
};

function getSalesLeadInfoItems(lead: SalesLeadView, attributes: ObjectAttributeValueWithField[]): LabelValueViewItem[] {
	const description = nullIfBlank(lead.description);
	return [
		description != null && {
			type: "section",
			label: i18n.t("description"),
			fields: [
				{
					label: null,
					value: description,
					multiline: true,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("general"),
			fields: [
				{
					label: i18n.t("customer_type"),
					value: getCustomerTypeLabel(lead.customerType),
				},
				{
					label: i18n.t("sales_pipeline"),
					value: lead.salesPipelineName,
				},
				{
					label: i18n.t("sales_pipeline_phase_entered_at"),
					value:
						lead.salesPipelinePhaseEnteredAt != null ?
							formatIsoString(lead.salesPipelinePhaseEnteredAt)
						:	null,
				},
				{
					label: i18n.t("lead_source"),
					value: lead.salesLeadSourceName,
				},
				{
					label: i18n.t("sales_area"),
					value: lead.salesAreaName,
				},
				{
					label: i18n.t("customer_group"),
					value: lead.customerGroupName,
				},
				{
					label: i18n.t("offer"),
					value: lead.customerOrderOfferId,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("salesperson"),
			fields: [
				{
					label: i18n.t("phone"),
					value: lead.salespersonPhone,
				},
				{
					label: i18n.t("email"),
					value: lead.salespersonEmail,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("contact_person"),
			fields: [
				{
					label: i18n.t("name"),
					value: nullIfBlank(lead.contactName),
				},
				{
					label: i18n.t("email"),
					value: nullIfBlank(lead.contactEmail),
				},
				{
					label: i18n.t("phone"),
					value: nullIfBlank(lead.contactPhone),
				},
			],
		},
		{
			type: "section",
			label: i18n.t("address"),
			fields: [
				{
					label: i18n.t("address_1"),
					value: nullIfBlank(lead.address_1),
				},
				{
					label: i18n.t("address_2"),
					value: nullIfBlank(lead.address_2),
				},
				{
					label: i18n.t("postal_code"),
					value: nullIfBlank(lead.postalCode),
				},
				{
					label: i18n.t("city"),
					value: nullIfBlank(lead.city),
				},
				{
					label: i18n.t("country"),
					value: getCountryLabel(lead.country),
				},
			],
		},
		{
			type: "section",
			label: i18n.t("events"),
			fields: [
				{
					label: i18n.t("created_at"),
					value: formatIsoString(lead.createdAt),
				},
				{
					label: i18n.t("created_by"),
					value: lead.createdByUserName,
				},
				{
					label: i18n.t("last_updated"),
					value: formatIsoString(lead.lastUpdated),
				},
				...(lead.wonAt != null ?
					[
						{
							label: i18n.t("won_at"),
							value: formatIsoString(lead.wonAt),
						},
					]
				:	[]),
				...(lead.lostAt != null ?
					[
						{
							label: i18n.t("lost_at"),
							value: formatIsoString(lead.lostAt),
						},
					]
				:	[]),
				...(nullIfBlank(lead.lostReason) != null ?
					[
						{
							label: i18n.t("lose_reason"),
							value: lead.lostReason,
							multiline: true,
						},
					]
				:	[]),
				...(lead.discardedAt != null ?
					[
						{
							label: i18n.t("discarded_at"),
							value: formatIsoString(lead.discardedAt),
						},
						{
							label: i18n.t("discarded_by"),
							value: lead.discardedByUserName,
						},
					]
				:	[]),
				...(nullIfBlank(lead.discardedReason) != null ?
					[
						{
							label: i18n.t("discard_reason"),
							value: lead.discardedReason,
							multiline: true,
						},
					]
				:	[]),
			],
		},
		getObjectAttributeLabelValueSection(attributes),
	];
}
